import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { dashboardAPI } from '../api/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, Euro, Building, Building2, Printer, 
  TrendingUp, DollarSign, Calendar, ShoppingCart, Wrench, Package
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ title, value, icon: Icon, color, prefix = '', suffix = '', delay = 0, themeColor }) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const duration = 1000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setAnimatedValue(value);
          clearInterval(timer);
        } else {
          setAnimatedValue(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return (
    <Card 
      className="border-l-4 hover:shadow-xl transition-all duration-300 hover:scale-105 animate-in fade-in-0 slide-in-from-bottom-4" 
      style={{ borderLeftColor: themeColor || color, animationDelay: `${delay}ms` }}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">
              {prefix}{typeof animatedValue === 'number' ? animatedValue.toLocaleString('el-GR') : animatedValue}{suffix}
            </p>
          </div>
          <div className="p-4 rounded-full" style={{ backgroundColor: `${themeColor}20` }}>
            <Icon className="h-8 w-8" style={{ color: themeColor }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const { currentColors } = useTheme();
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (availableYears.length > 0) {
      fetchStats();
    }
  }, [selectedYear, availableYears]);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/readings/available-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = response.data.years || [];
      setAvailableYears(years);
      
      // Set selected year to the most recent year if not already in the list
      if (years.length > 0 && !years.includes(selectedYear)) {
        setSelectedYear(years[0]);
      }
    } catch (error) {
      console.error('Error fetching available years:', error);
      // Fallback to current year
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear]);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.getStats(selectedYear);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-r-transparent mb-4" style={{ borderColor: currentColors.primary, borderRightColor: 'transparent' }}></div>
          <p className="text-gray-600">Φόρτωση στατιστικών...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in-0 duration-500">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Συνοπτική Εικόνα</h1>
          <p className="text-sm sm:text-base text-gray-600">Επισκόπηση στατιστικών και οικονομικών στοιχείων</p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-gray-500" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 font-medium focus:outline-none focus:ring-2 transition-all"
            style={{ '--tw-ring-color': currentColors.primary }}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {stats && (
        <>
          {/* Pages Stats */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">📄 Σελίδες Εκτύπωσης</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Σελίδες A4 BLACK"
                value={stats.total_pages_black}
                icon={FileText}
                color="border-l-gray-500"
                themeColor={currentColors.primary}
                delay={0}
              />
              <StatCard
                title="Σελίδες A4 COLOR"
                value={stats.total_pages_color}
                icon={FileText}
                color="border-l-blue-500"
                themeColor={currentColors.accent}
                delay={100}
              />
              <StatCard
                title="Σελίδες A3 BLACK"
                value={stats.total_pages_a3}
                icon={FileText}
                color="border-l-gray-600"
                themeColor={currentColors.primaryDark}
                delay={200}
              />
              <StatCard
                title="Σελίδες A3 COLOR"
                value={stats.total_pages_a3_color}
                icon={FileText}
                color="border-l-cyan-500"
                themeColor={currentColors.accent}
                delay={300}
              />
            </div>
          </div>

          {/* Financial Stats */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">💰 Οικονομικά Στοιχεία</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Αξία A4 Black"
                value={stats.total_cost_black}
                icon={Euro}
                color="border-l-green-500"
                themeColor="#059669"
                suffix=" €"
                delay={400}
              />
              <StatCard
                title="Αξία A4 Color"
                value={stats.total_cost_color}
                icon={Euro}
                color="border-l-emerald-500"
                themeColor="#10b981"
                suffix=" €"
                delay={500}
              />
              <StatCard
                title="Αξία A3 Black"
                value={stats.total_cost_a3}
                icon={Euro}
                color="border-l-teal-500"
                themeColor="#14b8a6"
                suffix=" €"
                delay={600}
              />
              <StatCard
                title="Αξία A3 Color"
                value={stats.total_cost_a3_color}
                icon={Euro}
                color="border-l-blue-600"
                themeColor={currentColors.primary}
                suffix=" €"
                delay={700}
              />
            </div>
          </div>

          {/* Summary Stats */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">📈 Σύνολα</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Σύνολο Αξίας Σελίδων"
                value={stats.total_pages_cost}
                icon={DollarSign}
                color="border-l-purple-500"
                themeColor="#7c3aed"
                suffix=" €"
                delay={800}
              />
              <StatCard
                title="Σύνολο Παγίων"
                value={stats.total_monthly_fees}
                icon={TrendingUp}
                color="border-l-pink-500"
                themeColor="#ec4899"
                suffix=" €"
                delay={900}
              />
              <StatCard
                title="Ενεργά Μηχανήματα"
                value={stats.active_machines}
                icon={Printer}
                color="border-l-orange-500"
                themeColor="#f97316"
                delay={1000}
              />
              <StatCard
                title="Ενεργοί Πελάτες"
                value={stats.active_customers}
                icon={Building}
                color="border-l-red-500"
                themeColor="#ef4444"
                delay={1100}
              />
            </div>
          </div>

          {/* Annual Expenses */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">📦 Έξοδα Έτους {selectedYear}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Κόστος Αναλωσίμων"
                value={stats.total_consumables_cost || 0}
                icon={ShoppingCart}
                color="border-l-amber-500"
                themeColor="#d97706"
                suffix=" €"
                delay={1200}
              />
              <StatCard
                title="Κόστος Ανταλλακτικών"
                value={stats.total_parts_cost || 0}
                icon={Package}
                color="border-l-rose-500"
                themeColor="#e11d48"
                suffix=" €"
                delay={1300}
              />
              <StatCard
                title="Κόστος Εργασίας"
                value={stats.total_labor_cost || 0}
                icon={Wrench}
                color="border-l-sky-500"
                themeColor="#0284c7"
                suffix=" €"
                delay={1400}
              />
              <StatCard
                title="Σύνολο Εξόδων"
                value={stats.total_expenses || 0}
                icon={DollarSign}
                color="border-l-red-600"
                themeColor="#dc2626"
                suffix=" €"
                delay={1500}
              />
              <StatCard
                title={`Κόστος Στόλου${stats.fleet_machines_count ? ` (${stats.fleet_machines_count})` : ''}`}
                value={stats.total_fleet_cost || 0}
                icon={Building2}
                color="border-l-purple-600"
                themeColor="#7c3aed"
                suffix=" €"
                delay={1600}
              />
            </div>
          </div>

          {/* Total Revenue - Featured */}
          <Card 
            className="text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-[1.02] animate-in fade-in-0 zoom-in-95" 
            style={{ 
              background: `linear-gradient(to bottom right, ${currentColors.primary}, ${currentColors.primaryDark})`,
              animationDelay: '1600ms' 
            }}
          >
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-full">
                  <TrendingUp className="h-8 w-8" />
                </div>
                Συνολικά Έσοδα Έτους {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl sm:text-5xl font-bold mb-2">
                {stats.total_revenue.toLocaleString('el-GR', { 
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2 
                })} €
              </p>
              <p className="text-white/80 text-sm sm:text-base">
                Σελίδες: {stats.total_pages_cost.toLocaleString('el-GR', { minimumFractionDigits: 2 })} € + 
                Πάγια: {stats.total_monthly_fees.toLocaleString('el-GR', { minimumFractionDigits: 2 })} €
              </p>
              {(stats.total_expenses > 0) && (
                <div className="mt-4 pt-4 border-t border-white/30">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Σύνολο Εξόδων:</span>
                    <span className="text-lg font-semibold">-{(stats.total_expenses || 0).toLocaleString('el-GR', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-white/80 font-bold text-lg">Καθαρό Αποτέλεσμα:</span>
                    <span className="text-2xl font-bold">
                      {(stats.total_revenue - (stats.total_expenses || 0)).toLocaleString('el-GR', { minimumFractionDigits: 2 })} €
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
